import { useCallback, useEffect, useRef, useState } from "react";
import useMarketPlacesAutocomplete from "../../hooks/useMarketPlacesAutocomplete";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import { Container, Wrapper } from "../../components/Layout/checkoutLayout";
import mapStyles from "./mapStyles";
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from "@reach/combobox";
import "@reach/combobox/styles.css";
import HealthcareNavBar from "../../components/HealthcareNavBar";
import CheckoutStepper from "../../components/CheckoutStepper";
import { saveShippingAddress } from "../../redux/healthcareCartSlice";
import SavedAddressPicker from "../../components/Address/SavedAddressPicker";
import ClearIcon from "@mui/icons-material/Clear";
import { isWithinMarketBounds, MAP_CENTER, normalizeMarketLatLng } from "../../config/market";
import { resolveInitialDeliveryPhone, saveLastDeliveryPhone, accountPhone } from "../../utils/deliveryPhone";
import { updateMyProfile } from "../../api/profileApi";
import { loginSuccess } from "../../redux/userRedux";
import { toast } from "react-toastify";
import useCoverage from "../../redux/useCoverage";
import { cartOutletId } from "../../utils/vendorOutlets";
import CoverageBanner from "../../components/CoverageBanner";
import GoogleMapBoundary from "../../components/Maps/GoogleMapBoundary";
import MapPin from "../../components/Maps/MapPin";
import { geocodeAddress, latLngFromGeocode, reverseGeocode } from "../../utils/googlePlaces";
import reportClientError from "../../utils/clientTelemetry";
import {
  evaluateShippingGate,
  savedAddressLatLng,
  shippingGateLabel,
} from "../../utils/shippingValidation";

const Button = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: #E91E63;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  &:hover {
    background-color: #C2185B;
  }
  &:disabled {
    background-color: #f48fb1;
    color: #fff;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #aaa;
  &:hover {
    color: #E91E63;
  }
`;

// Page background + card chrome are shared — see components/Layout/checkoutLayout.

const Title = styled.h1`
  font-size: 24px;
  font-weight: 300;
  font-family: "Poppins", sans-serif;
  color: #E91E63;
`;

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
`;

const Input = styled.input`
  flex: 1;
  min-width: 40%;
  margin: 10px 0px;
  padding: 10px;
  border: 1px solid #E91E63;
  border-radius: 8px;
  outline: none;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.9);
  background-color: #e2e2e2c4;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: mapStyles,
};

function HealthcareShippingScreen() {
  const { vendorId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const cart = useSelector((state) => state.healthcareCart.carts[vendorId] || {});
  const vendor = useSelector((state) => state.healthcareCart.vendor);

  const [center, setCenter] = useState(MAP_CENTER);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasChosenAddress, setHasChosenAddress] = useState(false);
  const [saveToProfile, setSaveToProfile] = useState(false);
  const [addressError, setAddressError] = useState("");
  const userLocation = useSelector((state) => state.userLocation);

    // An EXPLICIT pick outranks the saved primary. `locationSource:'manual'` is
    // set only by setManualAddress — the shopper typed and chose a place. A
    // gps/ip guess is a much weaker signal about where they want a delivery, so
    // the saved primary still wins there.
    const browseLocationIsExplicit =
        userLocation?.locationSource === "manual" &&
        isWithinMarketBounds(userLocation?.latitude, userLocation?.longitude);
  const currentUser = useSelector((state) => state.user?.currentUser);

  const coverage = useCoverage({
    vendorId,
    // A pharmacy branch (4 Oct 2026) delivers from ITS own address and
    // radius; the cart names it on every line.
    outletId: cartOutletId(cart),
    vendorType: "healthcare",
    enabled: hasChosenAddress,
    lat: markerPosition?.lat,
    lng: markerPosition?.lng,
  });

  const mapRef = useRef();
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    error: placesError,
  } = useMarketPlacesAutocomplete({ debounce: 200 });

  useEffect(() => {
    if (placesError) {
      setAddressError(
        "Address search is temporarily unavailable. Retry the map or use a valid saved address."
      );
    }
  }, [placesError]);

  const phonePrefilledRef = useRef(false);
  useEffect(() => {
    if (phonePrefilledRef.current) return;
    const prefill = resolveInitialDeliveryPhone(currentUser);
    if (prefill) {
      phonePrefilledRef.current = true;
      setPhoneNumber(String(prefill));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    const rawLat = userLocation?.latitude;
    const rawLng = userLocation?.longitude;
    if (isWithinMarketBounds(rawLat, rawLng)) {
      const lat = Number(rawLat);
      const lng = Number(rawLng);
      prefilledRef.current = true;
      setCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
      setAddress(userLocation.address || "Your set location");
      setValue(userLocation.address || "", false);
      setHasChosenAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation?.latitude, userLocation?.longitude]);

  const handleSelect = async (selectedAddress) => {
    setValue(selectedAddress, false);
    clearSuggestions();

    try {
      const results = await geocodeAddress(selectedAddress);
      const raw = latLngFromGeocode(results[0]);
      const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!coordinates) throw new Error("The selected address is outside this delivery market");
      setCenter(coordinates);
      setMarkerPosition(coordinates);
      setAddress(selectedAddress);
      setSelectedPlace({ name: selectedAddress, position: coordinates });
      setHasChosenAddress(true);
      setAddressError("");

      if (mapRef.current) {
        mapRef.current.panTo(coordinates);
      }
    } catch (error) {
      reportClientError(error, { area: "healthcare_shipping_geocode" });
      setHasChosenAddress(false);
      setAddressError("We couldn’t pin that address. Choose another suggestion and retry.");
    }
  };

  const handleMapClick = useCallback(async (event) => {
    const coordinates = normalizeMarketLatLng(event.latLng.lat(), event.latLng.lng());
    if (!coordinates) {
      setAddressError("Choose a location within this delivery market.");
      return;
    }

    try {
      const results = await reverseGeocode(coordinates.lat, coordinates.lng);
      if (results[0]) {
        const resolvedAddress = results[0].formatted_address;
        setMarkerPosition(coordinates);
        setCenter(coordinates);
        setAddress(resolvedAddress);
        setValue(resolvedAddress, false);
        setSelectedPlace({ name: resolvedAddress, position: coordinates });
        setHasChosenAddress(true);
        setAddressError("");
      }
    } catch (error) {
      reportClientError(error, { area: "healthcare_reverse_geocode" });
      setAddressError("We couldn’t resolve that map point. Choose another point and retry.");
    }
  }, [setValue]);

  // Pre-fill the manual form from a chosen saved address (its latLng then
  // flows into saveShippingAddress for the coverage gate + delivery fee).
  const handleSelectSavedAddress = (addr) => {
    if (!addr) return;
    const coordinates = savedAddressLatLng(addr);
    if (!coordinates) {
      setHasChosenAddress(false);
      setAddressError("This saved address needs an updated location pin before checkout.");
      return;
    }
    if (addr.recipientPhone) setPhoneNumber(addr.recipientPhone);
    setAddress(addr.address || addr.description || "");
    setValue(addr.address || "", false);
    setCenter(coordinates);
    setMarkerPosition(coordinates);
    setSelectedPlace({ name: addr.label || addr.address || "Saved address", position: coordinates });
    setHasChosenAddress(true);
    setAddressError("");
  };

  const clearChosenAddress = () => {
    setAddress("");
    setCenter(MAP_CENTER);
    setMarkerPosition(null);
    setSelectedPlace(null);
    setValue("");
    clearSuggestions();
    setHasChosenAddress(false);
    setAddressError("");
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (!canContinue) return;
    saveLastDeliveryPhone(phoneNumber);
    if (saveToProfile && phoneNumber.replace(/\D/g, "").length >= 9) {
      updateMyProfile({ phoneNumber })
        .then((data) => {
          // Only refresh the redux user when a token is available — dispatching
          // loginSuccess without one falls through to the raw-payload branch of
          // the reducer and corrupts currentUser into a nested shape.
          const refreshedToken =
            data.accessToken ||
            currentUser?.accessToken ||
            currentUser?.token ||
            currentUser?.result?.accessToken ||
            currentUser?.result?.token;
          if (refreshedToken) {
            dispatch(loginSuccess({
              user: { ...currentUser, ...(data.user || {}), phoneNumber },
              accessToken: refreshedToken,
            }));
          }
          toast.success("Saved your number to your profile.");
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || "Couldn't save the number to your profile, but your order will still use it.");
        });
    }

    dispatch(
      saveShippingAddress({
        locationName: { name: address },
        customerNumber: phoneNumber,
        latLng: { lat: markerPosition.lat, lng: markerPosition.lng },
        lat: markerPosition.lat,
        lng: markerPosition.lng,
      })
    );

    history.push(`/healthcare/payment/${vendorId}`);
  };

  const cartCount = Object.values(cart).reduce((total, item) => total + item.quantity, 0);

  const shippingGate = evaluateShippingGate({
    phone: phoneNumber,
    address,
    lat: markerPosition?.lat,
    lng: markerPosition?.lng,
    hasChosenAddress,
    vendorId,
    coverage,
  });
  const canContinue = shippingGate.canContinue;

  return (
    <>
      <HealthcareNavBar
        vendor={vendor}
        cartCount={cartCount}
      />
      <Container>
        <Wrapper>
          <CheckoutStepper currentStep="shipping" />
          <Title>Delivery Address</Title>
          <SavedAddressPicker
            autoSelect={!browseLocationIsExplicit}
            onSelect={handleSelectSavedAddress}
            onDeselect={clearChosenAddress}
          />
          <Form>
            <Combobox onSelect={handleSelect}>
              <InputWrapper style={{ marginBottom: "10px" }}>
                <ComboboxInput
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setAddressError("");
                  }}
                  disabled={!ready}
                  placeholder={ready ? "Search for your location..." : "Loading address search…"}
                  style={{
                    width: "100%",
                    padding: "10px 36px 10px 10px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    border: "1px solid #E91E63",
                    boxSizing: "border-box",
                  }}
                />
                {value && (
                  <ClearButton type="button" onClick={() => { setValue(""); clearSuggestions(); }}>
                    <ClearIcon fontSize="small" />
                  </ClearButton>
                )}
              </InputWrapper>
              <ComboboxPopover>
                <ComboboxList>
                  {status === "OK" &&
                    data.map(({ place_id, description }) => (
                      <ComboboxOption key={place_id} value={description} />
                    ))}
                </ComboboxList>
              </ComboboxPopover>
            </Combobox>
            {addressError && (
              <p role="alert" style={{ color: "#b3362a", fontSize: "12px", margin: "0 0 8px" }}>
                {addressError}
              </p>
            )}

            <div style={{ marginBottom: "15px" }}>
              <GoogleMapBoundary height="400px">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={13}
                  center={center}
                  options={options}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                >
                {markerPosition && (
                  <MapPin
                    position={markerPosition}
                    color="#E91E63"
                    label="Selected delivery location"
                    onClick={() => setSelectedPlace({ name: address, position: markerPosition })}
                  />
                )}
                {selectedPlace && (
                  <InfoWindow
                    position={selectedPlace.position}
                    onCloseClick={() => setSelectedPlace(null)}
                  >
                    <div>
                      <h3>Selected Location</h3>
                      <p>{selectedPlace.name}</p>
                    </div>
                  </InfoWindow>
                )}
                </GoogleMap>
              </GoogleMapBoundary>
            </div>

            <InputWrapper>
              <Input
                placeholder="Phone Number *"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                style={{ paddingRight: "36px" }}
              />
              {phoneNumber && (
                <ClearButton type="button" onClick={() => setPhoneNumber("")}>
                  <ClearIcon fontSize="small" />
                </ClearButton>
              )}
            </InputWrapper>
            {phoneNumber.replace(/\D/g, "").length >= 9 &&
              phoneNumber.trim() !== String(accountPhone(currentUser) || "").trim() && (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    alignSelf: "flex-start",
                    gap: "8px",
                    fontSize: "13px",
                    color: "#334",
                    margin: "4px 2px 6px",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "auto",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={saveToProfile}
                    onChange={(e) => setSaveToProfile(e.target.checked)}
                    style={{ width: "16px", height: "16px", minWidth: "16px", margin: 0, flex: "none", cursor: "pointer" }}
                  />
                  <span>Save this number to my profile for next time</span>
                </label>
              )}
            <InputWrapper>
              <Input
                placeholder="Delivery Address (Auto-filled from map)"
                value={address}
                readOnly
                style={{ backgroundColor: "#f0f0f0", paddingRight: "36px" }}
              />
              {address && (
                <ClearButton type="button" onClick={clearChosenAddress}>
                  <ClearIcon fontSize="small" />
                </ClearButton>
              )}
            </InputWrapper>
            <CoverageBanner
              variant="block"
              deliverable={coverage.deliverable}
              reason={coverage.reason}
              distanceKm={coverage.distanceKm}
              radius={coverage.radius}
              hasLocation={coverage.hasLocation}
              status={coverage.status}
              error={coverage.error}
              onRetry={coverage.refresh}
            />
            <Button type="submit" onClick={submitHandler} disabled={!canContinue}>
              {shippingGateLabel(shippingGate.code)}
            </Button>
          </Form>
        </Wrapper>
      </Container>
    </>
  );
}

export default HealthcareShippingScreen;
